// PrincipalContext — bootstraps from GET /v1/auth/session and refreshes
// on the four triggers from frontend-design §1 (F-001 fix):
//   (a) any 401 → render the re-auth modal + full-page redirect
//   (b) window focus / visibilitychange-visible → re-probe; replace
//       Principal atomically if role differs
//   (c) 5-minute foreground-only interval probe (suppressed on blur)
//   (d) 2xx response from PATCH /v1/people/{id} where id ===
//       Principal.person_id → invoke (b) before render of toast
//
// Role-change observed: replace context atomically; TopNav re-renders;
// role-label live region announces; if new role removes access to the
// current route, navigate to the role's home + info toast.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, redirectToLogin } from '@/api/endpoints';
import { onUnauthorized } from '@/api/client';
import { ApiError } from '@/types/errors';
import type { Principal, Role } from '@/types/domain';
import { homeRouteFor } from '@/auth/permissions';
import { useToast } from '@/components/ToastHost';
import { Modal } from '@/components/ModalHost';

interface PrincipalState {
  principal: Principal | null;
  bootstrapping: boolean;
  bootstrapError: ApiError | null;
}

interface PrincipalContextValue extends PrincipalState {
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  // Notify the context that a self-PATCH on /v1/people/{id} succeeded
  // (trigger (d)). Returns the post-refresh principal so callers can
  // gate the next nav decision against the new role.
  onSelfPatched: () => Promise<Principal | null>;
}

const Context = createContext<PrincipalContextValue | null>(null);

const FOCUS_PROBE_DEBOUNCE_MS = 1500;
const INTERVAL_PROBE_MS = 5 * 60 * 1000;

// Routes that are role-gated client-side. If a role-change probe
// observes that the user no longer has access to the current path, we
// navigate them to their home route and toast.
function pathPermittedFor(path: string, role: Role): boolean {
  if (path.startsWith('/admin/')) return role === 'admin';
  if (path.startsWith('/dashboard')) return role === 'admin' || role === 'practice_lead';
  if (path === '/agreements/new') return role === 'admin' || role === 'practice_lead';
  if (path === '/agreements') return role === 'admin' || role === 'practice_lead';
  return true;
}

export function PrincipalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PrincipalState>({
    principal: null,
    bootstrapping: true,
    bootstrapError: null,
  });
  const inFlightRef = useRef<Promise<Principal | null> | null>(null);
  const lastFocusProbeAtRef = useRef<number>(0);
  const intervalRef = useRef<number | null>(null);
  // Re-auth modal: open on first 401 from a /v1/* call mid-session.
  // Subsequent 401s while the modal is already open are coalesced (the
  // user has not yet acted on the prompt; firing the same modal again
  // would just re-trigger focus management and produce an aria-live
  // re-announcement). The modal stays open until the user either
  // clicks "Sign in" (full-page redirect) or the page itself
  // unmounts.
  const [reauthOpen, setReauthOpen] = useState(false);
  const reauthOpenRef = useRef(false);
  const navigate = useNavigate();
  const toast = useToast();

  // Single underlying probe — coalesces concurrent requests.
  const probe = useCallback(async (): Promise<Principal | null> => {
    if (inFlightRef.current) return inFlightRef.current;
    const p = (async () => {
      try {
        const res = await authApi.session();
        return res.principal;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          // Caller will handle (the bootstrap path full-page-redirects;
          // an in-app probe will trigger via the unauthorized handler).
          throw err;
        }
        throw err;
      } finally {
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = p;
    return p;
  }, []);

  // Bootstrap on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const principal = await probe();
        if (cancelled) return;
        setState({ principal, bootstrapping: false, bootstrapError: null });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          // Bootstrap-401 → full-page redirect to login (no UI rendered).
          redirectToLogin();
          return;
        }
        setState({
          principal: null,
          bootstrapping: false,
          bootstrapError:
            err instanceof ApiError
              ? err
              : new ApiError(0, {
                  error_code: 'internal_error',
                  message: 'Failed to bootstrap session.',
                }),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [probe]);

  // Apply a probe result, handling role-change cascades. Returns the
  // new (or unchanged) principal.
  const applyProbeResult = useCallback(
    (next: Principal | null): Principal | null => {
      setState((prev) => {
        const oldRole = prev.principal?.role ?? null;
        if (next && oldRole !== next.role) {
          // Live-region announcement is wired in TopNav by reading the
          // Principal value; here we only also toast if access was lost.
          const path = window.location.pathname;
          if (!pathPermittedFor(path, next.role)) {
            // Defer the navigation to after the state commit so the new
            // principal is observed by route guards on next render.
            window.queueMicrotask(() => {
              toast.show({
                severity: 'info',
                message:
                  'Your role changed. You no longer have access to that page.',
              });
              navigate(homeRouteFor(next), { replace: true });
            });
          }
        }
        return { principal: next, bootstrapping: false, bootstrapError: null };
      });
      return next;
    },
    [navigate, toast],
  );

  // Trigger (b)/(c) — silent refresh probe. 5xx is swallowed (the next
  // probe will retry); 401 falls through to the unauthorized handler.
  const refresh = useCallback(async () => {
    try {
      const next = await probe();
      applyProbeResult(next);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Unauthorized handler fires from the API client itself.
        return;
      }
      // Silent — telemetry hook would log here.
    }
  }, [applyProbeResult, probe]);

  // Trigger (a) — any 401 from the /v1/* surface during normal use.
  // The unauthorized handler in the API client is fired before the
  // ApiError reaches the caller; per frontend-design §1 (Session
  // expiry mid-session) we render a single-affordance modal that
  // gates the full-page redirect on a user click — this preserves
  // the page's mounted state until the user is ready (so they can
  // copy a half-typed comment, abort to a different tab, etc.). We
  // also raise a complementary live-region toast so screen readers
  // announce the cause without depending on modal focus management.
  useEffect(() => {
    const off = onUnauthorized(() => {
      if (reauthOpenRef.current) return;
      reauthOpenRef.current = true;
      setReauthOpen(true);
      toast.show({
        severity: 'error',
        message: 'Your session has expired. Sign in again to continue.',
      });
    });
    return off;
  }, [toast]);

  const onReauthSignIn = useCallback(() => {
    // V1 cannot preserve form drafts across re-auth (frontend-design
    // §1 + G-FE-2). The redirect target is /v1/auth/login which the
    // backend 302s to Google.
    redirectToLogin();
  }, []);

  // Trigger (b) — focus / visibilitychange.
  useEffect(() => {
    if (state.bootstrapping || !state.principal) return;
    function maybeProbe() {
      const now = Date.now();
      if (now - lastFocusProbeAtRef.current < FOCUS_PROBE_DEBOUNCE_MS) return;
      lastFocusProbeAtRef.current = now;
      void refresh();
    }
    function onFocus() {
      maybeProbe();
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') maybeProbe();
    }
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, state.bootstrapping, state.principal]);

  // Trigger (c) — 5-minute foreground-only interval. Suspended when
  // tab is hidden; resumed on visibility change.
  useEffect(() => {
    if (state.bootstrapping || !state.principal) return;
    function start() {
      if (intervalRef.current !== null) return;
      intervalRef.current = window.setInterval(() => {
        void refresh();
      }, INTERVAL_PROBE_MS);
    }
    function stop() {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') start();
      else stop();
    }
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, state.bootstrapping, state.principal]);

  // Trigger (d) — self-PATCH success.
  const onSelfPatched = useCallback(async (): Promise<Principal | null> => {
    try {
      const next = await probe();
      return applyProbeResult(next);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      return state.principal;
    }
  }, [applyProbeResult, probe, state.principal]);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
      // Full-page navigate so the bootstrap re-runs and the now-session-
      // less app redirects to the login flow.
      window.location.href = '/';
    } catch {
      toast.show({
        severity: 'error',
        message: 'Sign-out failed. Try again.',
      });
    }
  }, [toast]);

  const value: PrincipalContextValue = {
    ...state,
    refresh,
    signOut,
    onSelfPatched,
  };
  return (
    <Context.Provider value={value}>
      {children}
      <ReauthModal open={reauthOpen} onSignIn={onReauthSignIn} />
    </Context.Provider>
  );
}

// frontend-design §1 Application Shell — Session expiry mid-session:
// "show a modal 'Your session has expired. Sign in again to continue.
// (Your unsaved changes will be lost.)' with a single 'Sign in'
// affordance that performs the full-page redirect." (G-FE-2 confirms
// V1 does not preserve form drafts across re-auth.) The modal is
// alertdialog (focus-trapped, scrim-not-dismissible) so the user
// cannot accidentally dismiss it; the only path forward is the
// "Sign in" button. ESC is also suppressed (scrimDismissDisabled) for
// the same reason. The modal stays mounted until the click triggers
// a full-page navigation, so there is no reauthOpen=false transition
// in normal use; the ref-based idempotency guard above coalesces any
// further 401s while it's open.
function ReauthModal({
  open,
  onSignIn,
}: {
  open: boolean;
  onSignIn: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={() => {
        /* The modal is non-dismissible — ignore close attempts other
           than the explicit "Sign in" button click. The canClose
           gate below also returns false to suppress the header X
           and any future programmatic close. */
      }}
      title="Your session has expired"
      role="alertdialog"
      scrimDismissDisabled
      canClose={() => false}
      hideCloseButton
      describedById="reauth-body"
    >
      <p id="reauth-body">
        Sign in again to continue. Your unsaved changes will be lost.
      </p>
      <div className="form-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onSignIn}
          autoFocus
        >
          Sign in
        </button>
      </div>
    </Modal>
  );
}

export function usePrincipalContext(): PrincipalContextValue {
  const v = useContext(Context);
  if (!v) {
    throw new Error('usePrincipalContext used outside <PrincipalProvider>');
  }
  return v;
}

// Convenience: get the current Principal or null.
export function usePrincipal(): Principal | null {
  return usePrincipalContext().principal;
}
